import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'components/UI/Button/Button';
import { ReactComponent as CrossIcon } from 'assets/icons/svgs/icon-cross.svg';
import { applyPromoCode, removePromoCode } from 'services/BookingModalService';
import { appConstants } from 'utils/global-constants';
import { isUserLoggedIn, getLoginRedirectUrl } from '../booking-modal-utils';

import './BookingModalPromo.scss';

export const BookingModalPromo = (props) => {
  const {
    prodSeasonId,
    performanceId,
    setEventData,
    membershipAccountUrl,
    promoCodeFromUrl,
    promocodeApplied,
    promocodeName,
  } = props;
  const [isProcessing, setIsProcessing] = useState(false);
  const [promoLoadedFromUrl, setPromoLoadedFromUrl] = useState(promoCodeFromUrl);
  const [inputText, setInputText] = useState(promoCodeFromUrl || '');
  const [promoCode, setPromoCode] = useState(promoCodeFromUrl || '');
  const [promoCodeError, setPromoCodeError] = useState(false);
  const [submitButtonIsDisabled, setSubmitButtonIsDisabled] = useState(true);

  const accountUrl = isUserLoggedIn(membershipAccountUrl)
    ? membershipAccountUrl
    : `${appConstants.loginUrl}?redirect_to=${getLoginRedirectUrl}`;

  const inputChangeHandler = (event) => {
    setInputText(event.target.value);
    if (event.target.value.length > 0) {
      setSubmitButtonIsDisabled(false);
    } else {
      setSubmitButtonIsDisabled(true);
    }
  };

  const addPromoCodeHandler = async () => {
    const applyPromocodePayload = {
      promoCode: inputText,
      prodSeasonId,
      performanceId,
    };

    try {
      const response = await applyPromoCode(applyPromocodePayload);
      setSubmitButtonIsDisabled(false);
      setIsProcessing(false);
      setPromoLoadedFromUrl(false);

      const isCodeApplied = response?.PromoCodeApplied;
      const isErrorText = response?.PromoCodeErrorText;

      if (isCodeApplied) {
        setPromoCode(inputText);
        setEventData((prevState) => ({
          ...prevState,
          PerformanceDateTimes: [...response.PerformanceDateTimes],
          CalendarData: response.CalendarData,
          PromoCodeApplied: response.PromoCodeApplied,
          PromoCodeName: response.PromoCodeName,
        }));
        setPromoCodeError(false);
      } else if (isErrorText) {
        setPromoCodeError(isErrorText);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const removePromoCodeHandler = async () => {
    const removePromocodePayload = {
      prodSeasonId,
      performanceId,
    };

    try {
      const response = await removePromoCode(removePromocodePayload);
      const codeHasBeenRemoved = !response?.PromoCodeApplied;
      setSubmitButtonIsDisabled(true);
      setIsProcessing(false);

      if (codeHasBeenRemoved) {
        setEventData((prevState) => ({
          ...prevState,
          PerformanceDateTimes: [...response.PerformanceDateTimes],
          CalendarData: response.CalendarData,
          PromoCodeApplied: response.PromoCodeApplied,
          PromoCodeName: response.PromoCodeName,
        }));
        setPromoCode('');
        setInputText('');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const onSubmitPromoCodeHandler = (event) => {
    event.preventDefault();
    setSubmitButtonIsDisabled(true);
    setIsProcessing(true);
    const noPromoCodeAddedYet = !promoCode;

    if (noPromoCodeAddedYet) {
      addPromoCodeHandler();
    } else {
      removePromoCodeHandler();
    }
  };

  const renderRemovePromoButton = () => {
    let removePromoButton = (
      <button type="submit" className="promo-code__remove-promo-button">
        <CrossIcon className="cross-icon" />
        <span>Remove promo code</span>
      </button>
    );

    /*
     * We don't show this variation of the button IF the promocode was added automatically
     * via a url. This is to prevent this text showing which might cause the user to think
     * that the promo code is being removed during the processing stage.
     */
    if (isProcessing && !promoLoadedFromUrl) {
      removePromoButton = (
        <span className="promo-code__remove-promo-text">Removing promo code...</span>
      );
    }

    return removePromoButton;
  };

  useEffect(() => {
    setPromoCode(promocodeName);
  }, [promocodeApplied]);

  useEffect(() => {
    if (promoCodeFromUrl) {
      setInputText(promoCodeFromUrl);
      setSubmitButtonIsDisabled(true);
      setIsProcessing(true);
      addPromoCodeHandler();
    }
  }, [promoCodeFromUrl]);

  const promoCodeInput =
    !promoCode || promoCodeError ? (
      <input
        id="promoCode"
        name="promoCode"
        className={`promo-code__input form-control show-placeholder ${
          promoCodeError ? 'input-error' : ''
        }`}
        placeholder="Enter promo code"
        value={inputText}
        onChange={inputChangeHandler}
        data-testid="promo-input-field"
      />
    ) : (
      <p data-testid="promo-success-message">
        Great, we've applied your Promo code{' '}
        <span className="text-uppercase" data-testid="promo-code-value">
          {promoCode}
        </span>
        . It's now active for the specific performances and/or price reserves indicated in the offer
        you received.
        {renderRemovePromoButton()}
      </p>
    );

  const promoButton = (
    <Button type="submit" disabled={submitButtonIsDisabled} extendClass="promo-code__button">
      {!isProcessing ? 'Apply code' : 'Processing...'}
    </Button>
  );

  const promoCodeMessage = (
    <div className="promo-code__message">
      <h6 className="h6 hidden-xs">Don't have a promo code?</h6>
      <span className="visible-xs-inline">
        Don't have a promo code?{' '}
        <a href={accountUrl}>
          {isUserLoggedIn(membershipAccountUrl) ? 'Update your account' : 'Log in to your account'}
        </a>{' '}
        to subscribe to our newsletter and be in the know about coming events and special ticket
        offers.
      </span>
      <p className="hidden-xs">
        <a href={accountUrl}>
          {isUserLoggedIn(membershipAccountUrl) ? 'Update your account' : 'Log in to your account'}
        </a>{' '}
        to subscribe to our newsletter and be in the know about coming events and special ticket
        offers.
      </p>
    </div>
  );

  return (
    <div className="booking-modal__promo-code" data-testid="modal-promo">
      <div className="promo-code__form-container">
        <span className="promo-code__form-heading">Promo code</span>
        <form
          className="promo-code__form"
          onSubmit={onSubmitPromoCodeHandler}
          data-testid="promo-form"
        >
          <div className="promo-code__form__inner">
            {promoCodeInput}
            {!promoCode && promoButton}
          </div>
          {promoCodeError && <span className="promo-code__error-message">{promoCodeError}</span>}
        </form>
      </div>
      {promoCodeMessage}
    </div>
  );
};

BookingModalPromo.propTypes = {
  prodSeasonId: PropTypes.number,
  performanceId: PropTypes.number,
  setEventData: PropTypes.func,
  membershipAccountUrl: PropTypes.string,
  promoCodeFromUrl: PropTypes.string,
  promocodeApplied: PropTypes.bool,
  promocodeName: PropTypes.string,
};
